export type PortalConfig = {
  authority: string;
  clientId: string;
  apiBase: string;
  customers: string[];
  /**
   * When true, `customers` is empty at startup; the app calls
   * `GET .../usage?report=customers` after sign-in (requires API staff role).
   */
  customersFromApi: boolean;
  /** Required Keycloak realm role (`realm_access.roles`), e.g. `ross-cloudops`. */
  portalRealmRole: string;
  redirectUri: string;
};

function requireNonEmptyString(
  obj: Record<string, unknown>,
  key: string,
): string {
  const v = obj[key];
  if (v == null) {
    throw new Error(`config.json: missing "${key}"`);
  }
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  if (s === "") {
    throw new Error(`config.json: empty "${key}"`);
  }
  return s;
}

/**
 * Parse `/config.json` body. All fields are public (same as a Vite-env build);
 * never put secrets here.
 */
export function parsePortalConfigJson(
  data: unknown,
  location: { origin: string; pathname: string },
): PortalConfig {
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("config.json: root must be a JSON object");
  }
  const o = data as Record<string, unknown>;

  const keycloakUrl = requireNonEmptyString(o, "keycloakUrl").replace(
    /\/+$/,
    "",
  );
  const realm = requireNonEmptyString(o, "realm");
  const clientId = requireNonEmptyString(o, "clientId");
  const apiBase = requireNonEmptyString(o, "usageApiBase").replace(/\/+$/, "");

  const customersRaw = o.customers;
  let customers: string[];
  let customersFromApi = false;
  if (typeof customersRaw === "string") {
    const t = customersRaw.trim();
    if (!t) {
      throw new Error('config.json: "customers" must not be empty');
    }
    if (t.toLowerCase() === "auto") {
      customersFromApi = true;
      customers = [];
    } else {
      customers = t
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } else if (Array.isArray(customersRaw)) {
    customers = [];
    for (const x of customersRaw) {
      if (typeof x === "string") {
        const t = x.trim();
        if (t) customers.push(t);
      } else if (typeof x === "number" && Number.isFinite(x)) {
        customers.push(String(x));
      } else {
        throw new Error(
          'config.json: each "customers" entry must be a string or number',
        );
      }
    }
    if (customers.length === 0) {
      throw new Error(
        'config.json: "customers" array is empty — list ids or use the string "auto" to load them from the usage API',
      );
    }
  } else {
    throw new Error(
      'config.json: "customers" must be "auto", a comma-separated string, or a JSON array of strings',
    );
  }
  if (!customersFromApi && customers.length === 0) {
    throw new Error('config.json: "customers" must list at least one id');
  }

  const portalRealmRole =
    typeof o.portalRealmRole === "string" && o.portalRealmRole.trim()
      ? o.portalRealmRole.trim()
      : typeof o.portalAccessClaim === "string" && o.portalAccessClaim.trim()
        ? o.portalAccessClaim.trim()
        : "ross-cloudops";

  return {
    authority: `${keycloakUrl}/realms/${realm}`,
    clientId,
    apiBase,
    customers,
    customersFromApi,
    portalRealmRole,
    redirectUri: `${location.origin}${location.pathname}`,
  };
}
